import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Flex,
  Box,
  Text, // Text might not be directly used here anymore, but good to keep if ModalHeaderContent uses it
  useToast,
} from '@chakra-ui/react';
import { useSocket } from '../../../../contexts/SocketContext'; // Ensure path is correct
import { NewKBFollowUpModal } from './call-components'; // Ensure path is correct
// API service is now used within the modular files, so direct import here might not be needed unless for other specific uses.
// import api from '../../../../services/api';
import WhatsAppGroupInviteModal from './call-components/WhatsAppIntegration/WhatsAppGroupInviteModal'; // Ensure path is correct
import { WhatsAppGroupCreateModal } from './call-components/WhatsAppIntegration'; // Import the new modal
import ExtractInfoModal from './ai-extraction/ExtractInfoModal'; // Import the AI extraction modal
// aiDataService and saveExtractionResults are used within NewKBRecordCallModal-aiFeatures.js

// Modular imports
import NewKBRecordCallModalCallContent from './NewKBRecordCallModal-CallContent';
import NewKBRecordCallModalHeaderContent from './NewKBRecordCallModal-ModalHeaderContent';
import { calculateNextFollowUpDetailsForNoAnswer } from './NewKBRecordCallModal-utils';
import { fetchLeadCallHistory, saveLeadCustomerNotes } from './NewKBRecordCallModal-leadDataManagement';
import { extractInformationFromNotes, applyExtractedDataToLead } from './NewKBRecordCallModal-aiFeatures';
import { prepareBaseCallData, persistCallLog } from './NewKBRecordCallModal-callProcessing';

/**
 * Record Call Modal component specifically for the NewKB implementation
 * Handles recording call outcomes and updating lead information
 */
const NewKBRecordCallModal = ({ isOpen, onClose, lead, onSuccess, embedded = false }) => {
  const { socket, emitWithSourceId } = useSocket(); // socket & emitWithSourceId are not used in this snippet, consider removing if not needed elsewhere in this file
  const toast = useToast();

  // Local state (remains the same)
  const [callStatus, setCallStatus] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [callNotes, setCallNotes] = useState('');
  const [callHistory, setCallHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [customerNotes, setCustomerNotes] = useState(lead?.customerNotes || '');
  const [isFollowUpModalOpen, setIsFollowUpModalOpen] = useState(false);
  const [pendingCallData, setPendingCallData] = useState(null);
  const [isWhatsAppGroupInviteModalOpen, setIsWhatsAppGroupInviteModalOpen] = useState(false);
  const [isWhatsAppGroupCreateModalOpen, setIsWhatsAppGroupCreateModalOpen] = useState(false);
  
  // AI extraction state
  const [useAiExtraction, setUseAiExtraction] = useState(false);
  const [isExtractionModalOpen, setIsExtractionModalOpen] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [isExtracting, setIsExtracting] = useState(false);

  // Task-related state
  const [createTask, setCreateTask] = useState(false);
  const [taskData, setTaskData] = useState({
    title: '',
    priority: 'medium',
    dueDate: '',
    assignedTo: {
      email: ''
    }
  });

  // Memoized callback for fetching call history
  const doFetchCallHistory = useCallback(() => {
    if (lead) {
      fetchLeadCallHistory(lead, toast, setCallHistory, setIsLoadingHistory);
    }
  }, [lead, toast]); // Dependencies for useCallback

  // Reset form state when modal is opened with a new lead
  useEffect(() => {
    if (isOpen && lead) {
      setCallStatus('');
      setRescheduleTime('');
      setCallNotes('');
      setCustomerNotes(lead?.customerNotes || '');
      setCreateTask(false);
      setTaskData({
        title: '',
        priority: 'medium',
        dueDate: '',
        assignedTo: {
          email: ''
        }
      });
      setUseAiExtraction(false); // Reset AI toggle
      setExtractedData(null);
      doFetchCallHistory();
    }
  }, [isOpen, lead, doFetchCallHistory]);


  // Wrapper for saving customer notes
  const handleSaveCustomerNotes = async (notesToSave) => {
    await saveLeadCustomerNotes(notesToSave, lead, toast, setCustomerNotes, onSuccess);
  };

  // Reset form state
  const resetForm = useCallback(() => {
    setCallStatus('');
    setRescheduleTime('');
    setCallNotes('');
    setCreateTask(false);
    setTaskData({ title: '', priority: 'medium', dueDate: '', assignedTo: { email: '' } });
    setPendingCallData(null);
    setUseAiExtraction(false);
    // setExtractedData(null); // Don't reset extractedData here as the modal might still be open
  }, []); // No dependencies needed if setters are stable

  // Handle follow-up modal save
  const handleFollowUpSave = async (followUpTimeFromModal) => {
    if (!pendingCallData) return;

    let finalCallData = { ...pendingCallData };
    const needsAiExtraction = finalCallData.aiExtraction; // Check flag before deleting
    delete finalCallData.aiExtraction; 

    if (followUpTimeFromModal) {
      finalCallData.rescheduledTo = new Date(followUpTimeFromModal).toISOString();
      const followUpTimeFormatted = new Date(followUpTimeFromModal).toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata', hour: 'numeric', minute: 'numeric', hour12: true,
        year: 'numeric', month: 'short', day: 'numeric'
      });
      finalCallData.notes = (finalCallData.notes || '') + 
                           (finalCallData.notes ? ` - Follow up scheduled for ${followUpTimeFormatted}` :
                           `Follow up scheduled for ${followUpTimeFormatted}`);
    } else {
      finalCallData.rescheduledTo = null; // Explicitly clear reschedule time
      toast({
        title: 'Existing follow-up cleared',
        description: 'No new follow-up time was set.',
        status: 'info',
        duration: 3000,
      });
      finalCallData.notes = (finalCallData.notes || '') + 
                           (finalCallData.notes ? ' - Follow-up cleared' : 'Follow-up cleared');
    }

    setIsFollowUpModalOpen(false); // Close follow-up modal first

    const callSaved = await persistCallLog({
      callDataToSave: finalCallData,
      createTask,
      taskData,
      lead,
      toast,
      fetchCallHistoryCallback: doFetchCallHistory,
      resetFormCallback: resetForm,
      onSuccessCallback: onSuccess,
      onCloseModalCallback: onClose, // This closes the main modal
      setTaskDataCallback: setTaskData,
      setCreateTaskCallback: setCreateTask
    });
    
    if (callSaved && needsAiExtraction && finalCallData.notes.trim()) {
      await extractInformationFromNotes(
        finalCallData.notes, // Use the potentially updated notes
        lead,
        toast,
        setIsExtractionModalOpen,
        setExtractedData,
        setIsExtracting,
        isOpen // Check if main modal would still be open (though persistCallLog might close it)
               // This needs careful handling; AI extraction might need to happen BEFORE modal closes or be robust to it.
               // For now, pass isOpen, assuming persistCallLog's onClose is async or extractInfo is fast.
      );
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    const baseCallData = prepareBaseCallData(
      callStatus,
      callNotes,
      rescheduleTime, // Used by prepareBaseCallData if status is 'reschedule'
      lead,
      calculateNextFollowUpDetailsForNoAnswer,
      toast
    );

    if (!baseCallData) return; // Error/validation handled by prepareBaseCallData

    let callDataForPersistence = { ...baseCallData };
    let shouldOpenFollowUpModal = false;
    const processAiAfterSave = useAiExtraction && callNotes.trim();

    if (callStatus === 'yes') {
      // Augment baseCallData specifically for 'yes' outcome
      callDataForPersistence.outcome = 'answered'; // Already set by prepareBaseCallData
      callDataForPersistence.notes = callNotes.trim() || 'Call completed successfully';

      if (rescheduleTime) { // User manually set a reschedule time for the 'yes' call
        callDataForPersistence.rescheduledTo = new Date(rescheduleTime).toISOString();
        const followUpTimeFormatted = new Date(rescheduleTime).toLocaleString('en-IN', {
          timeZone: 'Asia/Kolkata', hour: 'numeric', minute: 'numeric', hour12: true,
          year: 'numeric', month: 'short', day: 'numeric'
        });
        callDataForPersistence.notes += (callDataForPersistence.notes ? ' - ' : '') +
                                       `Follow up scheduled for ${followUpTimeFormatted}`;
        toast({
          title: 'Call recorded with follow-up',
          description: `Next follow-up on ${followUpTimeFormatted}`,
          status: 'success',
          duration: 3000
        });
      } else { // No manual reschedule time for 'yes' call, open follow-up modal
        shouldOpenFollowUpModal = true;
        if (processAiAfterSave) {
          callDataForPersistence.aiExtraction = true; // Flag for handleFollowUpSave
        }
      }
    }

    if (shouldOpenFollowUpModal) {
      setPendingCallData(callDataForPersistence);
      setIsFollowUpModalOpen(true);
    } else {
      // Directly persist for 'no', 'reschedule', or 'yes' with manual reschedule
      const callSaved = await persistCallLog({
        callDataToSave: callDataForPersistence,
        createTask,
        taskData,
        lead,
        toast,
        fetchCallHistoryCallback: doFetchCallHistory,
        resetFormCallback: resetForm,
        onSuccessCallback: onSuccess,
        onCloseModalCallback: onClose, // persistCallLog calls this to close the main modal
        setTaskDataCallback: setTaskData,
        setCreateTaskCallback: setCreateTask
      });

      if (callSaved && processAiAfterSave) {
        await extractInformationFromNotes(
          callDataForPersistence.notes, // use the notes that were saved
          lead,
          toast,
          setIsExtractionModalOpen,
          setExtractedData,
          setIsExtracting,
          false // Main modal is likely closed or closing by persistCallLog
        );
      }
    }
  };
  
  const handleModalClose = () => {
    // resetForm(); // resetForm is called by persistCallLog or on useEffect when re-opened
    onClose();
  };

  const handleGroupAdded = (updatedLeadWithGroup) => {
    if (typeof onSuccess === 'function' && updatedLeadWithGroup) {
      onSuccess(updatedLeadWithGroup);
    }
    toast({
      title: 'WhatsApp Group Added',
      description: 'The WhatsApp group has been successfully added to this lead.',
      status: 'success',
      duration: 3000,
    });
  };
  
  const handleAiExtractionChange = (isChecked) => {
    setUseAiExtraction(isChecked);
  };

  const handleUpdateLeadWithExtractedData = async (dataToUpdate) => {
    return await applyExtractedDataToLead(dataToUpdate, lead, onSuccess, toast);
  };

  const callContentProps = {
    lead,
    callHistory,
    isLoadingHistory,
    onRefreshCallHistory: doFetchCallHistory,
    customerNotes,
    setCustomerNotes, // Direct setter for inline editing if any
    onSaveCustomerNotes: handleSaveCustomerNotes, // Use the wrapped handler
    callStatus,
    setCallStatus,
    rescheduleTime,
    setRescheduleTime,
    callNotes,
    setCallNotes,
    createTask,
    setCreateTask,
    taskData,
    setTaskData,
    setIsWhatsAppGroupInviteModalOpen,
    setIsWhatsAppGroupCreateModalOpen,
    embedded,
    useAiExtraction,
    onAiExtractionChange: handleAiExtractionChange,
    isExtractingAi: isExtracting, // Pass AI extracting state if needed by CallContent
  };

  if (embedded) {
    return (
      <Box p={4}>
        <Flex direction="column" width="100%">
          <NewKBRecordCallModalCallContent {...callContentProps} />
          <Flex justifyContent="flex-end" mt={4}>
            <Button colorScheme="blue" onClick={handleSubmit} isLoading={isExtracting}>
              Save Call Record
            </Button>
          </Flex>
        </Flex>
      </Box>
    );
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleModalClose} size="6xl">
        <ModalOverlay />
        <ModalContent maxW="1300px" sx={{
            transform: 'scale(0.9)', // Consider if this styling is still desired
            transformOrigin: 'center',
            margin: 'auto'
          }}>
          <ModalHeader>
            <NewKBRecordCallModalHeaderContent lead={lead} />
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <NewKBRecordCallModalCallContent {...callContentProps} />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleModalClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleSubmit} isLoading={isExtracting}> 
              Save Call Record
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <NewKBFollowUpModal
        isOpen={isFollowUpModalOpen}
        onClose={() => {
          setIsFollowUpModalOpen(false);
          if(pendingCallData && !pendingCallData.rescheduledTo){ // If it was a 'yes' call and user cancels follow-up
             toast({
                title: 'Follow-up selection cancelled',
                description: 'Call log not saved with new follow-up. You may need to re-save the call if desired.',
                status: 'info',
                duration: 3000,
             });
          }
          setPendingCallData(null); // Clear pending data if modal is closed without saving
        }}
        onSave={handleFollowUpSave}
        initialTime={rescheduleTime || pendingCallData?.rescheduledTo}
      />

      <WhatsAppGroupInviteModal
        isOpen={isWhatsAppGroupInviteModalOpen}
        onClose={() => setIsWhatsAppGroupInviteModalOpen(false)}
        lead={lead}
        onSuccess={handleGroupAdded}
      />

      <WhatsAppGroupCreateModal 
        isOpen={isWhatsAppGroupCreateModalOpen}
        onClose={() => setIsWhatsAppGroupCreateModalOpen(false)}
        lead={lead}
        onSuccess={handleGroupAdded}
      />
      
      <ExtractInfoModal
        isOpen={isExtractionModalOpen}
        onClose={() => {
            setIsExtractionModalOpen(false);
            setExtractedData(null); // Clear data when modal is closed
        }}
        leadData={lead} // Pass lead for context
        extractedData={extractedData}
        onUpdateLead={handleUpdateLeadWithExtractedData} // Use the new handler
      />
    </>
  );
};

export default NewKBRecordCallModal;